import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ViewHeader, PortalEmptyState } from "./PortalShared";
import { FileText, Plus, Send, CheckCircle, XCircle, RefreshCw, DollarSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  draft:     { label: "Draft",     color: "text-white/40 bg-white/[0.04] border-white/[0.08]" },
  sent:      { label: "Sent",      color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  accepted:  { label: "Accepted",  color: "text-[#00FF88] bg-emerald-500/10 border-emerald-500/20" },
  countered: { label: "Countered", color: "text-[#FF9F0A] bg-orange-500/10 border-orange-500/20" },
  rejected:  { label: "Rejected",  color: "text-[#FF3B30] bg-red-500/10 border-red-500/20" },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span className={`px-2 py-0.5 text-[10px] font-bold border rounded-full uppercase tracking-wider ${c.color}`}>{c.label}</span>;
}

const FINANCING_OPTIONS = ["conventional", "fha", "va", "cash", "hard_money", "seller_financing"];
const CONTINGENCIES = ["Inspection", "Financing", "Appraisal", "Title", "HOA Review", "Environmental"];

export default function OfferGenerator({ offers: initial = [] }) {
  const [offers, setOffers] = useState(initial);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    property_address: "", buyer_name: "", buyer_email: "",
    offer_price: "", earnest_money: "", down_payment_pct: 20,
    closing_date: "", financing_type: "conventional",
    inspection_days: 10, notes: ""
  });
  const [selectedContingencies, setSelectedContingencies] = useState([]);

  const toggleContingency = (c) => setSelectedContingencies(prev =>
    prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
  );

  const handleSubmit = async () => {
    if (!form.property_address || !form.buyer_name || !form.offer_price) {
      toast.error("Property, buyer name, and offer price are required"); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal-tools/offers`, {
        ...form,
        offer_price: parseFloat(form.offer_price) || 0,
        earnest_money: parseFloat(form.earnest_money) || 0,
        down_payment_pct: parseFloat(form.down_payment_pct) || 20,
        inspection_days: parseInt(form.inspection_days) || 10,
        contingencies: selectedContingencies,
      }, { withCredentials: true });
      setOffers(prev => [res.data, ...prev]);
      toast.success("Offer created");
      setShowForm(false);
      setForm({ property_address: "", buyer_name: "", buyer_email: "", offer_price: "", earnest_money: "", down_payment_pct: 20, closing_date: "", financing_type: "conventional", inspection_days: 10, notes: "" });
      setSelectedContingencies([]);
    } catch { toast.error("Failed to create offer"); }
    finally { setSaving(false); }
  };

  const updateStatus = async (offerId, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/portal-tools/offers/${offerId}/status`, { status }, { withCredentials: true });
      setOffers(prev => prev.map(o => o.offer_id === offerId ? { ...o, status } : o));
      toast.success(`Offer marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  if (showForm) return (
    <div>
      <ViewHeader title="New Offer" subtitle="Generate a professional buyer offer" action="Back" onAction={() => setShowForm(false)} />
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Offer Details</p>
          {[["property_address","Property Address"],["buyer_name","Buyer Name"],["buyer_email","Buyer Email","email"]].map(([k,l,t="text"]) => (
            <div key={k}>
              <label className="text-[10px] text-white/40 block mb-1">{l}</label>
              <Input type={t} value={form[k]} onChange={e => setForm(f=>({...f,[k]:e.target.value}))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
            </div>
          ))}
          {[["offer_price","Offer Price","number"],["earnest_money","Earnest Money","number"],["down_payment_pct","Down Payment %","number"],["inspection_days","Inspection Period (days)","number"],["closing_date","Target Closing Date","date"]].map(([k,l,t]) => (
            <div key={k}>
              <label className="text-[10px] text-white/40 block mb-1">{l}</label>
              <Input type={t} value={form[k]} onChange={e => setForm(f=>({...f,[k]:e.target.value}))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
            </div>
          ))}
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Financing Type</label>
            <select value={form.financing_type} onChange={e => setForm(f=>({...f,financing_type:e.target.value}))}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50">
              {FINANCING_OPTIONS.map(o => <option key={o} value={o} className="bg-[#0A0A0A]">{o.replace('_',' ').toUpperCase()}</option>)}
            </select>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Contingencies</p>
            <div className="grid grid-cols-2 gap-2">
              {CONTINGENCIES.map(c => (
                <button key={c} onClick={() => toggleContingency(c)}
                  className={`text-xs py-2 px-3 border rounded transition-all ${selectedContingencies.includes(c) ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]' : 'bg-white/[0.02] border-white/[0.06] text-white/40 hover:text-white'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-2">Notes</p>
            <textarea value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} rows={3}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-[#D4AF37]/50" />
          </div>
          {form.offer_price && (
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl p-4">
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Offer Summary</p>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-white/50">Offer Price</span><span className="text-white font-bold">${parseFloat(form.offer_price||0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Earnest Money</span><span className="text-[#D4AF37]">${parseFloat(form.earnest_money||0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Down Payment</span><span className="text-white">${((parseFloat(form.offer_price||0)*parseFloat(form.down_payment_pct||20))/100).toLocaleString()}</span></div>
              </div>
            </div>
          )}
          <Button onClick={handleSubmit} disabled={saving} className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold" data-testid="create-offer-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />} Generate Offer
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <ViewHeader title="Offer Generator" subtitle="Create and track buyer offers" action="New Offer" onAction={() => setShowForm(true)} />
      {offers.length === 0 ? (
        <PortalEmptyState icon={FileText} title="No Offers Yet" desc="Generate your first buyer offer to track deals" action="Create Offer" onAction={() => setShowForm(true)} />
      ) : (
        <div className="space-y-3">
          {offers.map(offer => (
            <div key={offer.offer_id} data-testid={`offer-row-${offer.offer_id}`} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{offer.property_address}</p>
                <p className="text-white/40 text-xs">{offer.buyer_name} · {offer.financing_type?.replace('_',' ')}</p>
                <p className="text-white/20 text-xs mt-0.5">{new Date(offer.created_at).toLocaleDateString()}</p>
              </div>
              <div className="text-right">
                <p className="text-[#D4AF37] font-bold text-lg">${(offer.offer_price||0).toLocaleString()}</p>
                <StatusBadge status={offer.status} />
              </div>
              <div className="flex gap-1.5 ml-2">
                {offer.status === "draft" && <button onClick={() => updateStatus(offer.offer_id,"sent")} className="text-xs px-2 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded hover:bg-blue-500/20"><Send className="w-3 h-3"/></button>}
                {offer.status === "sent" && <button onClick={() => updateStatus(offer.offer_id,"accepted")} className="text-xs px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 text-[#00FF88] rounded hover:bg-emerald-500/20"><CheckCircle className="w-3 h-3"/></button>}
                {offer.status === "sent" && <button onClick={() => updateStatus(offer.offer_id,"rejected")} className="text-xs px-2 py-1 bg-red-500/10 border border-red-500/20 text-[#FF3B30] rounded hover:bg-red-500/20"><XCircle className="w-3 h-3"/></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
